import React, { useRef, useEffect, useState } from "react";
import { apiCall } from "@/api/apiCall";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import DonutChart from "../components/charts/DonutChart";
import { useQueue } from "@/context/QueueContext";
import HeadingComponent from "../components/shared/heading";
import { processParams } from "../utils/paramsHelper";

const Kpi_Chart_13 = ({ year = "", month = "", monthNumber = "", contract = "", page = null,isRefresh = false }) => {
  const { enqueue } = useQueue();

  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "null") {
      return "0%";
    }
    return value;
  };

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setError(null);
      setLoading(true);

      const apiParams = processParams(page?.params || {}, { year, month, contract });

      const response = await enqueue(() => apiCall(
        "GET",
        `/board-pack/page-twelve`,
        apiParams,
        navigate,
                false,
                false,
                isRefresh
      ));

      if (!response?.success) {
        throw new Error(response?.message || "API returned unsuccessful response");
      }

      if (response?.data) {
        const transformedData = [];

        const contractKey = Object.keys(response.data)[0];
        const contractData = response.data[contractKey];

        const kpiKey = Object.keys(contractData)[0];
        const kpiData = contractData[kpiKey];

        const years = Object.keys(kpiData).filter((key) => key !== "isPercent");

        years.forEach((year) => {
          const yearData = kpiData[year];
          const months = [
            { m: "Jan", v: formatValue(yearData.Jan) },
            { m: "Feb", v: formatValue(yearData.Feb) },
            { m: "Mar", v: formatValue(yearData.Mar) },
            { m: "Apr", v: formatValue(yearData.Apr) },
            { m: "May", v: formatValue(yearData.May) },
            { m: "Jun", v: formatValue(yearData.Jun) },
            { m: "Jul", v: formatValue(yearData.Jul) },
            { m: "Aug", v: formatValue(yearData.Aug) },
            { m: "Sep", v: formatValue(yearData.Sep) },
            { m: "Oct", v: formatValue(yearData.Oct) },
            { m: "Nov", v: formatValue(yearData.Nov) },
            { m: "Dec", v: formatValue(yearData.Dec) },
          ];

          // FY key detect karo response se
          const fyKey = Object.keys(yearData).find(
            (k) => k !== "Jan" && k !== "Feb" && k !== "Mar" && k !== "Apr" &&
                   k !== "May" && k !== "Jun" && k !== "Jul" && k !== "Aug" &&
                   k !== "Sep" && k !== "Oct" && k !== "Nov" && k !== "Dec"
          );

          transformedData.push({
            year: year,
            months: months,
            fullYear: formatValue(yearData[fyKey]),
            fullYearKey: fyKey || "FY",
          });
        });

        setData(transformedData);
      } else {
        setError(response?.message || "Failed to load data. Please try again later.");
      }
    } catch (error) {
      console.error("Error fetching occupancy data:", error);
      setError(error.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} onRetry={fetchData} />;

  const title = page?.title;
  const subtitle = page?.subtitle;
  const in_chart_content_line_1 = page?.in_chart_content_line_1;

  return (
    <div className="px-6 mt-10 mb-16 w-full text-card-foreground">

      {/* Header */}
      <div className="flex flex-col gap-1 text-center mb-3">
        {title && (
          <HeadingComponent variant={"main"}>
            {title}
          </HeadingComponent>
        )}
        {subtitle && (
          <HeadingComponent variant={"sub"}>
            {subtitle}
          </HeadingComponent>
        )}
        {in_chart_content_line_1 && (
          <div className="mt-3 flex justify-center">
            <span
              className="border border-1 px-6 py-2 text-xs text-white bg-blue-900 font-semibold tracking-wide"
              style={{ whiteSpace: 'nowrap' }}
            >
              {in_chart_content_line_1}
            </span>
          </div>
        )}
      </div>

      {/* Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr) 100px',
          border: '1px solid #d1d5db',
          width: '100%',
        }}
      >
        {data.map((row) => (
          <React.Fragment key={row.year}>

            {/* 12 Month cells */}
            {row.months.map((mo, idx) => (
              <div
                key={idx}
                style={{
                  height: '160px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  border: '1px solid #d1d5db',
                }}
              >
                {/* Year badge — shown on Jul cell */}
                {mo.m === 'Jul' && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: '#1e3a5f',
                      color: '#ffffff',
                      fontSize: '8px',
                      fontWeight: '700',
                      padding: '2px 10px',
                      borderRadius: '3px',
                      whiteSpace: 'nowrap',
                      zIndex: 10,
                    }}
                  >
                    {row.year}
                  </div>
                )}

                <DonutChart value={mo.v} label={mo.m} showLabel={true} size={65} />
              </div>
            ))}

            {/* Full Year cell */}
            <div
              style={{
                width: '100px',
                minWidth: '100px',
                height: '160px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#c2bfbf',
                border: '1px solid #d1d5db',
                overflow: 'hidden',
                padding: '0 4px',
              }}
            >
              <DonutChart
                value={row.fullYear}
                label={row.fullYearKey}
                showLabel={true}
                isFullYear={true}
                size={90}
              />
            </div>

          </React.Fragment>
        ))}
      </div>

    </div>
  );
};

export default Kpi_Chart_13;